/**
 * CSV 导出的小工具（纯函数 + 浏览器下载）。
 *
 * 客户/工厂报价单现在既能打印成 PDF，也能导出 CSV —— **同一份数据、同一套口径**，
 * 只是换一种载体：CSV 用 Excel 打开、能筛选排序、能塞进别的表格里。
 *
 * 几个必须守住的点：
 * - **UTF-8 BOM**：不加 BOM，Excel 打开中文会乱码；
 * - 逗号、引号、换行都要转义（材料名里真有逗号，比如 "Blum hardware, 15% of ..."）；
 * - 换行用 CRLF，Excel 最省心；
 * - 单元格里是**纯数字**（金额不带货币符号），币种写在表头里，方便在 Excel 里求和。
 */

/** 一个单元格：字符串、数字，或留空 */
export type CsvCell = string | number | null | undefined;

function escapeCell(cell: CsvCell): string {
  if (cell === null || cell === undefined) return "";
  const text = typeof cell === "number" ? String(cell) : cell;
  // 逗号 / 引号 / 换行 / 首尾空格 都要包引号，里面的引号翻倍
  if (/[",\r\n]/.test(text) || text !== text.trim()) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/** 行数组 → CSV 文本（带 UTF-8 BOM，Excel 直接双击就能看） */
export function toCsv(rows: CsvCell[][]): string {
  const body = rows.map((row) => row.map(escapeCell).join(",")).join("\r\n");
  return `\uFEFF${body}\r\n`;
}

/** 金额：整数分 → 两位小数的纯数字（CSV 里不带货币符号） */
export function centsToPlain(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** 触发浏览器下载；文件名沿用打印页那套「方案名 + 日期时间」 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * 导出文件名：和"打印/存 PDF"保持一致（方案名 + 日期时间，精确到分钟，防重名）。
 * 文件名里不能用的字符换短横，时间里的冒号写成短横。
 */
export function exportFileBase(planName: string, now: Date, suffix = ""): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(
    now.getHours(),
  )}-${pad(now.getMinutes())}`;
  const safe = planName.replace(/[\\/:*?"<>|]/g, "-").trim();
  return `${safe} ${stamp}${suffix}`;
}
